import React, { useState, useEffect, useCallback, useRef } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import type { RegistrationsReportFilters } from "../../hooks/useRegistrationsReport";

interface RegistrationsReportFiltersProps {
  filters: RegistrationsReportFilters;
  isExporting: boolean;
  onFilterChange: (
    key: keyof RegistrationsReportFilters,
    value: string | string[] | undefined,
  ) => void;
  onExportCsv: () => void;
}

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "present", label: "Present" },
  { value: "no_show", label: "No Show" },
];

const RegistrationsReportFilters: React.FC<RegistrationsReportFiltersProps> = ({
  filters,
  isExporting,
  onFilterChange,
  onExportCsv,
}) => {
  const [searchTerm, setSearchTerm] = useState(filters.searchTerm ?? "");
  const [status, setStatus] = useState(filters.statuses?.[0] ?? "");
  const [sessionDateFrom, setSessionDateFrom] = useState(
    filters.sessionDateFrom ?? "",
  );
  const [sessionDateTo, setSessionDateTo] = useState(
    filters.sessionDateTo ?? "",
  );

  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    onFilterChange("searchTerm", debouncedSearchTerm || undefined);
  }, [debouncedSearchTerm, onFilterChange]);

  const handleStatusChange = (value: string) => {
    if (value === "all") {
      setStatus("");
      onFilterChange("statuses", undefined);
    } else {
      setStatus(value);
      onFilterChange("statuses", [value]);
    }
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateFrom(e.target.value);
    onFilterChange("sessionDateFrom", e.target.value || undefined);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionDateTo(e.target.value);
    onFilterChange("sessionDateTo", e.target.value || undefined);
  };

  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setStatus("");
    setSessionDateFrom("");
    setSessionDateTo("");
    onFilterChange("searchTerm", undefined);
    onFilterChange("statuses", undefined);
    onFilterChange("sessionDateFrom", undefined);
    onFilterChange("sessionDateTo", undefined);
  }, [onFilterChange]);

  const hasActiveFilters = searchTerm || status || sessionDateFrom || sessionDateTo;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <div>
          <Label htmlFor="searchTerm">Search</Label>
          <Input
            className="mt-1.5 bg-white"
            id="searchTerm"
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Name, company, event..."
            type="text"
            value={searchTerm}
          />
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Select onValueChange={handleStatusChange} value={status || "all"}>
            <SelectTrigger className="mt-1.5 bg-white" id="status">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="sessionDateFrom">Session Date From</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateFrom"
            onChange={handleDateFromChange}
            type="date"
            value={sessionDateFrom}
          />
        </div>

        <div>
          <Label htmlFor="sessionDateTo">Session Date To</Label>
          <Input
            className="mt-1.5 bg-white"
            id="sessionDateTo"
            onChange={handleDateToChange}
            type="date"
            value={sessionDateTo}
          />
        </div>

        <div className="flex items-end gap-2">
          <Button
            className="border-gray-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            disabled={!hasActiveFilters}
            onClick={handleClearFilters}
            variant="outline"
          >
            Clear Filters
          </Button>
          <Button
            className="bg-green-600 hover:bg-green-700"
            disabled={isExporting}
            onClick={onExportCsv}
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export CSV"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RegistrationsReportFilters;
